import type { Entity } from '../components'
import type { GameWorld } from '../world'
import type { EventBus } from '../events'
import { playerCanEat } from '../growth'
import { dnaForRadius } from './damage'
import { FEAT_TEAM_HALO } from '../species'
import { T } from '../tunables'

export function collisionSystem(world: GameWorld, player: Entity, events: EventBus): void {
  if (!player.renderable) return
  const pr = player.renderable.radius
  const recruitReach = player.mods?.recruitReachAdd ?? 0
  // Osmotic Reach (power): eating works at a distance — reach, not magnetism.
  const eatReach = player.mods?.eatReachAdd ?? 0
  let allyCount = [...world.with('ally')].length

  for (const e of [...world.with('consumable', 'position', 'renderable')]) {
    if (e.absorbing) continue

    const dx = e.position.x - player.position.x
    const dy = e.position.y - player.position.y
    const dist2 = dx * dx + dy * dy
    const rSum = pr + e.renderable.radius + eatReach
    // Bouquet extends recruit-conversion reach for recruitables only.
    const reachR = e.recruitable ? rSum + recruitReach : rSum
    if (dist2 < reachR * reachR) {
      // Recruitable branch: recruit if under ally cap, otherwise eat
      if (e.recruitable) {
        if (allyCount < T.ally.maxAllies) {
          allyCount++
          world.removeComponent(e, 'consumable')
          world.removeComponent(e, 'recruitable')
          world.addComponent(e, 'ally', {
            followTimer: 45,
            orbitAngle: Math.random() * Math.PI * 2,
            state: 'orbiting' as const,
            targetPredator: null,
            damage: 2,
          })
          world.addComponent(e, 'health', { current: 3, max: 3 })
          if (e.morphology) e.morphology.featureFlags |= FEAT_TEAM_HALO
          events.emit({ type: 'ally.converted', ally: e })
          continue
        }
      }

      // Size-gated whole-swallow: a big enough — or engulfing — player
      // gulps an entire colony at once instead of nibbling it cell-by-cell.
      if (e.colonyMember && qualifiesForWholeSwallow(player, e)) {
        wholeSwallow(world, e.colonyMember.parent)
        continue
      }
      startAbsorb(world, e)
    }
  }

  // Reversal (VS study): a predator small enough relative to the player is
  // just food with opinions. Contact starts the same absorb animation;
  // stripping the predator component halts its AI and damage interactions.
  for (const e of [...world.with('predator', 'position', 'renderable')]) {
    if (e.absorbing || e.chainSegment) continue
    if (!playerCanEat(player, e)) continue
    const dx = e.position.x - player.position.x
    const dy = e.position.y - player.position.y
    const rSum = pr + e.renderable.radius + eatReach
    if (dx * dx + dy * dy < rSum * rSum) {
      world.removeComponent(e, 'predator')
      world.addComponent(e, 'absorbing', {
        progress: 0,
        duration: 0.20,
        startRadius: e.renderable.radius,
        startPos: { x: e.position.x, y: e.position.y },
        dnaValue: dnaForRadius(e.renderable.radius),
        growthUnits: T.growth.predatorGrowthUnits,
      })
      events.emit({ type: 'predator.eaten', predator: e })
    }
  }
}

// Start the standard absorb on a consumable, honoring vault duration and any
// bounty override (colony core, etc.). An undefined dnaValue falls through to
// the radius formula (with depth/mutation mults) in absorptionSystem.
function startAbsorb(world: GameWorld, e: Entity): void {
  const vault = e.behavior?.kind === 'vault'
  const dnaValue = e.bounty ?? (vault ? T.creatures.vault.dna : undefined)
  world.addComponent(e, 'absorbing', {
    progress: 0,
    duration: vault ? T.creatures.vault.absorbDuration : 0.20,
    startRadius: e.renderable!.radius,
    startPos: { x: e.position!.x, y: e.position!.y },
    dnaValue,
  })
}

function qualifiesForWholeSwallow(player: Entity, member: Entity): boolean {
  if ((player.engulf?.activeRemaining ?? 0) > 0) return true
  const pr = player.renderable?.radius ?? 0
  const mr = member.renderable?.radius ?? 1
  return pr >= mr * T.colony.wholeSwallowRatio
}

// Gulp every member of a colony at once — the growth payoff.
function wholeSwallow(world: GameWorld, parent: Entity): void {
  const members = [...world.with('colonyMember', 'position', 'renderable')]
    .filter(m => m.colonyMember!.parent === parent)
  for (const m of members) {
    if (!m.absorbing) startAbsorb(world, m)
  }
}
